import * as React from "react";
import { createPortal } from "react-dom";

// AIDEV-NOTE: Compound component pattern for clean, self-documenting API

interface ParallelLoadingContextValue {
  shouldShowContent: boolean;
  overlayContent?: React.ReactNode;
  overlayStyling?: React.CSSProperties;
}

interface ParallelLoadingContainerProps {
  children: React.ReactNode;
  shouldShowContent: boolean;
  overlayContent?: React.ReactNode;
  overlayStyling?: React.CSSProperties;
}

const ParallelLoadingContext =
  React.createContext<ParallelLoadingContextValue | null>(null);

const useParallelLoadingContext = () => {
  const context = React.useContext(ParallelLoadingContext);
  if (!context) {
    throw new Error("Component must be used within ParallelLoadingContainer");
  }
  return context;
};

const ParallelLoadingContainer: React.FC<ParallelLoadingContainerProps> & {
  Content: React.FC<{ children: React.ReactNode }>;
  Overlay: React.FC<{ text?: string }>;
} = ({ children, shouldShowContent, overlayContent, overlayStyling }) => {
  return (
    <ParallelLoadingContext.Provider
      value={{ shouldShowContent, overlayContent, overlayStyling }}
    >
      <div style={{ position: "relative", height: "100%" }}>{children}</div>
    </ParallelLoadingContext.Provider>
  );
};

// Compound component parts
ParallelLoadingContainer.Content = ({ children }) => {
  const { shouldShowContent } = useParallelLoadingContext();

  return (
    <div
      style={{
        visibility: shouldShowContent ? "visible" : "hidden",
        opacity: shouldShowContent ? 1 : 0,
        height: "100%",
        width: "100%",
        transition: shouldShowContent ? "opacity 0.2s ease-in-out" : "none",
        // AIDEV-NOTE: Always render content for parallel data fetching
        pointerEvents: shouldShowContent ? "auto" : "none",
      }}
    >
      {children}
    </div>
  );
};

ParallelLoadingContainer.Overlay = ({ text }) => {
  const { shouldShowContent, overlayContent, overlayStyling } =
    useParallelLoadingContext();

  // Only show overlay when content should be hidden
  if (shouldShowContent) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // zIndex: 1000,
        // AIDEV-NOTE: Ensure overlay blocks interaction with hidden content
        pointerEvents: "auto",
        ...overlayStyling,
      }}
    >
      {overlayContent || text}
    </div>,
    document.body
  );
};

export { ParallelLoadingContainer };
